import React from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import {
    DaTable,
    DaTableBody,
    DaTableCell,
    DaTableHeadCell,
    DaTableHead,
    DaTableRow,
    EyeIcon,
    Text,
    OutIcon,
    InternalGrid,
} from '@paygreen/paygreen-ui';
import { IsBigDesktop, truncateTooLongName } from '../../../utils/tools';
import { StatusDisplayTable, HideAndCopy } from '../../../components';
import { detectStripeStatus } from '../shared/tools';
import { visibilityStatus } from '../../../utils/constants';

import {
    visibilityStatusOptions,
    activationStatusOptions,
    accountStatusOptions,
    stripeAccountStatusOptions,
    getNameIdAccountTeam,
    actionCellboxProps,
    actionIconProps,
    widthCellboxProps,
} from '../shared';

const AdminCharitiesDaTable = ({
    t,
    data,
    isLoading,
    teamMembers,
    getTableProps,
    headerGroups,
    getTableBodyProps,
    page,
    prepareRow,
}) => {
    const isBigDesktop = IsBigDesktop();

    return (
        <InternalGrid
            displayType="grid"
            gridGap="md"
            gridTemplateColumns="1fr"
            blockPadding="none"
            justifyItems="stretch"
            hasStaticWidth={IsBigDesktop() ? false : true}
        >
            <DaTable
                {...getTableProps()}
                blockWidth="none"
                isLoading={isLoading}
                loadingRowNumber={30}
                loadingMainCellNumber={3}
            >
                {headerGroups.map((headerGroup) => (
                    <DaTableHead
                        blockTitle={t('table.title')}
                        {...headerGroup.getHeaderGroupProps()}
                    >
                        {headerGroup.headers.map((column, index) => (
                            <DaTableHeadCell
                                key={index}
                                label={column.render('Header')}
                            >
                                {![2, 4, 7].includes(index) &&
                                    column.render('Filter')}
                            </DaTableHeadCell>
                        ))}
                    </DaTableHead>
                ))}

                <DaTableBody {...getTableBodyProps()}>
                    {!isLoading && data.length && page.length ? (
                        page.map((row, index) => {
                            prepareRow(row);
                            const {
                                association,
                                user,
                                createdAt,
                                enabledAt,
                                stripeAccount,
                                accountManager,
                                visible,
                            } = row.values;

                            return (
                                <DaTableRow {...row.getRowProps()} key={index}>
                                    <DaTableCell
                                        label={t(
                                            'admin.users.table.organisationName',
                                        )}
                                        title={association[0]}
                                        {...widthCellboxProps(
                                            isBigDesktop,
                                            'sm',
                                        )}
                                    >
                                        {truncateTooLongName(
                                            association[0],
                                            80,
                                        )}

                                        {association[1] && (
                                            <a
                                                href={association[1]}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="hideOnMobile"
                                            >
                                                <OutIcon
                                                    marginLeft="sm"
                                                    title={association[1]}
                                                    {...actionIconProps(true)}
                                                />
                                            </a>
                                        )}
                                    </DaTableCell>

                                    <DaTableCell
                                        label={t('admin.users.table.user')}
                                        isMain={false}
                                        {...widthCellboxProps(
                                            isBigDesktop,
                                            'lg',
                                        )}
                                    >
                                        {user[0]} {user[1]}
                                        <InternalGrid
                                            displayType="grid"
                                            gridTemplateColumns="auto auto"
                                        >
                                            {user[2] && (
                                                <HideAndCopy
                                                    title={
                                                        t(
                                                            'admin.users.table.copyPhone',
                                                        ) + user[2]
                                                    }
                                                    hasHiddenText={false}
                                                    icon="PhoneIcon"
                                                    text={user[2]}
                                                />
                                            )}

                                            {user[3] && (
                                                <HideAndCopy
                                                    title={
                                                        t(
                                                            'admin.users.table.copyEmail',
                                                        ) + user[3]
                                                    }
                                                    hasHiddenText={false}
                                                    icon="LetterIcon"
                                                    text={user[3]}
                                                />
                                            )}
                                        </InternalGrid>
                                    </DaTableCell>

                                    <DaTableCell
                                        label={t(
                                            'admin.users.table.creationDate',
                                        )}
                                        isMain={false}
                                    >
                                        {createdAt}
                                    </DaTableCell>

                                    <DaTableCell
                                        label={t(
                                            'admin.users.table.activationDate',
                                        )}
                                        isMain={false}
                                    >
                                        <StatusDisplayTable
                                            status={
                                                enabledAt[0] === '0'
                                                    ? 'CREATED'
                                                    : 'VALIDATED'
                                            }
                                            statusOptions={
                                                activationStatusOptions
                                            }
                                            i18TextBase="admin.users.table.activationStatusFilter"
                                        />
                                    </DaTableCell>

                                    <DaTableCell
                                        label={t(
                                            'admin.charities.table.visible',
                                        )}
                                        isMain={false}
                                    >
                                        <StatusDisplayTable
                                            status={
                                                !!Number(visible)
                                                    ? visibilityStatus.VISIBLE
                                                    : visibilityStatus.NOTVISIBLE
                                            }
                                            statusOptions={
                                                visibilityStatusOptions
                                            }
                                            i18TextBase="admin.charities.table.visibilityStatusFilter"
                                        />
                                    </DaTableCell>

                                    <DaTableCell
                                        label={t(
                                            'admin.users.table.activationDate',
                                        )}
                                        isMain={false}
                                    >
                                        <StatusDisplayTable
                                            status={detectStripeStatus(
                                                stripeAccount[0],
                                                stripeAccount[1],
                                                stripeAccount[2],
                                            )}
                                            statusOptions={
                                                stripeAccountStatusOptions
                                            }
                                            i18TextBase="admin.charities.table.stripeAccountStatusFilter"
                                        />
                                    </DaTableCell>

                                    <DaTableCell
                                        label={t(
                                            'admin.users.table.accountManager',
                                        )}
                                        isMain={false}
                                    >
                                        {accountManager ? (
                                            getNameIdAccountTeam(
                                                Number(accountManager),
                                                teamMembers,
                                            )
                                        ) : (
                                            <StatusDisplayTable
                                                status="EMPTY"
                                                statusOptions={
                                                    accountStatusOptions
                                                }
                                                i18TextBase="admin.users.table.accountStatusFilter"
                                            />
                                        )}
                                    </DaTableCell>

                                    <DaTableCell
                                        label="Actions"
                                        {...actionCellboxProps(isBigDesktop)}
                                    >
                                        <RouterLink
                                            to={{
                                                pathname: `/admin/charities/${association[2]}`,
                                            }}
                                        >
                                            <EyeIcon
                                                title={t(
                                                    'admin.users.table.titleDetails',
                                                )}
                                                {...actionIconProps(true)}
                                            />
                                        </RouterLink>
                                    </DaTableCell>
                                </DaTableRow>
                            );
                        })
                    ) : (
                        <Text
                            align="center"
                            marginTop="xl"
                            marginBottom="xl"
                            textSize="sm"
                        >
                            {t('noDataFound')}
                        </Text>
                    )}
                </DaTableBody>
            </DaTable>
        </InternalGrid>
    );
};

AdminCharitiesDaTable.propTypes = {
    data: PropTypes.array.isRequired,
    isLoading: PropTypes.bool,
    teamMembers: PropTypes.array.isRequired,
    getTableProps: PropTypes.func.isRequired,
    headerGroups: PropTypes.array.isRequired,
    getTableBodyProps: PropTypes.func.isRequired,
    page: PropTypes.array.isRequired,
    prepareRow: PropTypes.func.isRequired,
};

AdminCharitiesDaTable.defaultProps = {
    isLoading: false,
};

export default withTranslation('common')(AdminCharitiesDaTable);
